// chats.js — aggregateChats: the lease/event/heartbeat join that
// powers the Chats page card list and the sparkline.
//
// Reads state.leasesAll, state.events, and state.heartbeats; merges
// per agent_id (claude-code/<uuid>) into a single chat record with
// recency, file touch count, status flags.
//
// Pure transformation. No DOM, no fetch. Inputs come from
// state.js, outputs are an array of chat objects sorted newest-first.

import { state } from "./state.js";
import { parseUTC } from "./util.js";
import { friendlyResource, repoFor } from "./format.js";
import { memberName } from "./members.js";

// aggregateChats returns an array of chat records sorted by
// mostRecentTs descending. Each chat record:
//   {
//     agentID:       "claude-code/<uuid>",
//     laptopID:      string (best-effort from lease or heartbeat),
//     leases:        Lease[] (full lease records for this chat),
//     files:         Map<friendlyResource, count>,
//     mostRecentTs:  number (ms),
//     totalLeases:   number,
//     held:          number (currently-held leases),
//     browsing:      boolean (heartbeat within 60s),
//     lastFile:      string,
//     lastTool:      string,
//   }
//
// Window: last 1 hour. Chats older than that are dropped (the UI
// shows "active in the last hour" by design — see chatDescription
// in format.js for the status-string mapping).
export function aggregateChats() {
  const byAgent = new Map();
  const oneHourAgo = Date.now() - 60 * 60 * 1000;

  for (const l of state.leasesAll) {
    // parseUTC handles both ISO-with-Z and D1's space-separated form.
    // Without it, the coordinator's "YYYY-MM-DD HH:MM:SS" timestamps
    // parse as local time and the 1-hour window drops fresh chats
    // for users east of GMT.
    const acquiredAt = parseUTC(l.AcquiredAt);
    if (!Number.isFinite(acquiredAt) || acquiredAt < oneHourAgo) continue;

    // laptop_id casing: the local daemon emits lowercase `laptop_id`;
    // the cloud /v1/teams/leases reshape emits PascalCase `LaptopID`
    // (added in the team-bird's-eye backend PR so chats can resolve to
    // a member). Read either so the member join works in both modes.
    const leaseLaptop = l.LaptopID || l.laptop_id || "";
    if (!byAgent.has(l.AgentID)) {
      byAgent.set(l.AgentID, {
        agentID: l.AgentID,
        laptopID: leaseLaptop,
        leases: [],
        files: new Map(),
        mostRecentTs: 0,
        totalLeases: 0,
        held: 0,
        browsing: false,
        lastFile: "",
        lastTool: "",
        editCount: 0,
      });
    }
    const a = byAgent.get(l.AgentID);
    if (!a.laptopID && leaseLaptop) a.laptopID = leaseLaptop;
    a.leases.push(l);
    a.totalLeases++;
    if (l.State === "held") a.held++;
    if (acquiredAt > a.mostRecentTs) a.mostRecentTs = acquiredAt;
    const sub = isFileResource(l.Resource) ? friendlyResource(l.Resource) : "";
    if (sub) a.files.set(sub, (a.files.get(sub) || 0) + 1);
  }

  // Merge in chats from coordination_events that don't yet appear
  // in the held-leases set (e.g. they had a recent lease.acquired
  // that has since been released — the chat is still "active" in
  // the timeline sense, just not currently writing).
  //
  // We derive chats ONLY from events whose agent_id is a real chat
  // ID (claude-code/<uuid>). Machine-level laptop_ids would falsely
  // show up as "browsing" chats forever — that was the persistent-
  // browsing bug pre-2026-04-25.
  const recentCutoff = Date.now() - 60 * 60 * 1000; // 1 hour
  for (const ev of state.events || []) {
    const aid = ev.AgentID || ev.agent_id || "";
    if (!aid.startsWith("claude-code/")) continue;
    const ts = parseUTC(ev.OccurredAt || ev.occurred_at);
    if (!ts || ts < recentCutoff) continue;
    const resource = ev.Resource || ev.resource || "";
    const evt = ev.EventType || ev.event_type || "";
    let a = byAgent.get(aid);
    if (!a) {
      a = {
        agentID: aid,
        laptopID: "",
        leases: [],
        files: new Map(),
        mostRecentTs: ts,
        totalLeases: 0,
        held: 0,
        browsing: false,
        lastFile: "",
        lastTool: "",
        editCount: 0,
      };
      byAgent.set(aid, a);
    }
    if (ts > a.mostRecentTs) a.mostRecentTs = ts;
    // Only an acquisition is a file this chat edited; notes, messages,
    // blocks and worktree events name other things. One already counted
    // from its lease row (still in state.leasesAll) is skipped so an edit
    // isn't counted twice. lastFile stays the heartbeat's: it is shown as
    // "Last read", which an acquisition is not.
    if (evt !== "lease.acquired" || !isFileResource(resource)) continue;
    if (a.leases.some((l) => sameLease(l, ev, resource, ts))) continue;
    const sub = friendlyResource(resource);
    if (sub) a.files.set(sub, (a.files.get(sub) || 0) + 1);
    a.totalLeases++;
  }

  // Merge in activity heartbeats. These tell us about chats that
  // have a recent PreToolUse signal (Read/Grep/Bash/Edit/Write) but
  // may not have any lease activity in the last hour — exactly the
  // "Browsing" / "Reading" state the local UI shows. browsing=true
  // if last_seen within 60 seconds.
  const browsingCutoffSecs = 60;
  for (const hb of state.heartbeats || []) {
    const aid = hb.agent_id || "";
    if (!aid.startsWith("claude-code/")) continue;
    // Use parseUTC so D1's space-separated "YYYY-MM-DD HH:MM:SS"
    // format gets correctly treated as UTC. See parseUTC docstring.
    const ts = parseUTC(hb.last_seen);
    if (!Number.isFinite(ts)) continue;
    let a = byAgent.get(aid);
    if (!a) {
      a = {
        agentID: aid,
        laptopID: hb.laptop_id || "",
        leases: [],
        files: new Map(),
        mostRecentTs: ts,
        totalLeases: 0,
        held: 0,
        browsing: false,
        lastFile: "",
        lastTool: "",
        editCount: 0,
      };
      byAgent.set(aid, a);
    }
    if (!a.laptopID && hb.laptop_id) a.laptopID = hb.laptop_id;
    if (ts > a.mostRecentTs) a.mostRecentTs = ts;
    // Durable edit count from the heartbeat (writes-table-sourced). Survives
    // lease expiry, so a chat that edited files but whose leases lapsed still
    // shows real edits instead of "0 edits". max() so a stale 0 never lowers it.
    a.editCount = Math.max(a.editCount || 0, hb.editCount || 0);
    if (hb.lastFile) {
      a.lastFile = hb.lastFile;
      // Also include heartbeat file in the files map so
      // inferChatLabel has something to label by even when no
      // lease ever fired.
      a.files.set(hb.lastFile, (a.files.get(hb.lastFile) || 0) + 1);
    }
    if (hb.lastTool) a.lastTool = hb.lastTool;
    // Within the browsing window? Mark the chat as live so the UI
    // can render "Browsing"/"Reading" instead of going INACTIVE.
    const ageSecs =
      typeof hb.age_seconds === "number" && hb.age_seconds >= 0
        ? hb.age_seconds
        : Math.floor((Date.now() - ts) / 1000);
    if (ageSecs <= browsingCutoffSecs) {
      a.browsing = true;
      // The heartbeat's own count covers files the held-lease list missed.
      a.held = Math.max(a.held, hb.heldLeases || 0);
    }
  }

  return Array.from(byAgent.values()).sort(
    (a, b) => b.mostRecentTs - a.mostRecentTs,
  );
}

// chatStatus: "active" while the chat holds files it is editing, "browsing"
// while it is working without holding any (reading, running commands,
// thinking), "idle" when quiet for under 10 minutes, "gone" after that.
export function chatStatus(c, now = Date.now()) {
  if (c.held > 0) return "active";
  if (c.browsing) return "browsing";
  return now - c.mostRecentTs < 10 * 60 * 1000 ? "idle" : "gone";
}

// isFileResource: "file:" resources, or a hosted row's decrypted path, which
// carries no "<kind>:" prefix. note:, msg:, dir:, branch: and the like are
// not files a chat edited.
function isFileResource(r) {
  if (!r) return false;
  return /^[a-z_]+:/.test(r) ? r.startsWith("file:") : true;
}

// sameLease matches a lease.acquired event to the lease row it created: by
// lease id when the event carries one, else by file and acquisition time.
function sameLease(l, ev, resource, ts) {
  const p = ev.Payload || ev.payload || {};
  if (p.lease_id && l.LeaseID) return p.lease_id === l.LeaseID;
  return l.Resource === resource && Math.abs(parseUTC(l.AcquiredAt) - ts) < 5000;
}

// resolveChatMember returns the team-member display name for a chat's
// agent_id, via laptop_id → member (memberName scans state.people /
// state.members). Returns "" when the chat can't be attributed (no
// laptop_id, anonymous/legacy session, or pre-v0.38 row) so callers can
// fall back to the anonymous tool/shortID label. Used by the Overview
// + Chats "Team" views to label chats by WHO instead of a uuid.
export function resolveChatMember(agentID) {
  const laptopID = laptopForAgent(agentID);
  if (!laptopID) return "";
  const name = memberName(laptopID);
  // memberName falls back to returning the laptop_id itself when it
  // can't resolve a person; treat that as "unresolved" for labeling.
  return name && name !== laptopID ? name : "";
}

// resolveChatRepo returns a best-effort repo label for a chat's most
// recent lease (decrypted .Repo, else derived from the resource/
// purpose). "" when unknown. Used alongside resolveChatMember in Team
// views to show WHAT repo each member is in.
export function resolveChatRepo(agentID) {
  let best = null;
  for (const l of state.leasesAll || []) {
    if (l.AgentID !== agentID) continue;
    const ts = parseUTC(l.AcquiredAt);
    if (!best || ts > best.ts) best = { lease: l, ts };
  }
  if (!best) return "";
  const l = best.lease;
  return repoFor(l.Resource || "", l.Purpose || "", l.Repo || "") || "";
}

// laptopForAgent resolves an agent_id → laptop_id from the data
// aggregateChats already merges: a held/recent lease's LaptopID (cloud)
// or laptop_id (local), else a heartbeat row. Kept separate from
// aggregateChats so legend/label code can resolve without rebuilding
// the whole chat list.
function laptopForAgent(agentID) {
  for (const l of state.leasesAll || []) {
    if (l.AgentID === agentID && (l.LaptopID || l.laptop_id)) {
      return l.LaptopID || l.laptop_id;
    }
  }
  for (const hb of state.heartbeats || []) {
    if (hb.agent_id === agentID && hb.laptop_id) return hb.laptop_id;
  }
  return "";
}
